/**
 * React hook for fetching persona profiles.
 * Provides catalog data for memory calculations.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchPersonaRows } from '@/lib/catalog/personas';
import { fetchPersonaProfiles } from '@/lib/catalog/personaFetcher';
import type { PersonaRow } from '@/lib/types/catalog';
import type { PersonaProfile } from '@/types/personas';

interface PersonaCatalogData {
  personas: PersonaRow[];
}

interface UsePersonaCatalogOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch persona profiles.
 * 
 * @param options - Configuration options
 * @param options.enabled - Whether to enable the query (default: true)
 * @returns Query result with personas data
 */
export function usePersonaCatalog(options: UsePersonaCatalogOptions = {}) {
  const { enabled = true } = options;

  return useQuery<PersonaCatalogData, Error>({
    queryKey: ['persona-catalog'],
    queryFn: async (): Promise<PersonaCatalogData> => {
      const personas = await fetchPersonaRows();
      return { personas };
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}

/**
 * Hook to fetch full persona profiles with all fields from unified personas table.
 * Use this for human dynamics calculations that require read_sec_default, think_sec_default, etc.
 */
export function usePersonaProfiles() {
  return useQuery<PersonaProfile[], Error>({
    queryKey: ['persona-profiles-full'],
    queryFn: fetchPersonaProfiles,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
